import React, { useState, useEffect, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FiArrowLeft,
  FiArrowRight,
  FiCalendar,
  FiChevronLeft,
  FiChevronRight,
  FiCode,
  FiEye,
  FiExternalLink,
  FiGithub,
} from "react-icons/fi";
import { projectsAPI, getImageUrl } from "../services/api";
import LoadingSpinner from "../components/LoadingSpinner";

const ProjectDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [relatedProjects, setRelatedProjects] = useState([]);

  useEffect(() => {
    setCurrentImageIndex(0);
  }, [id]);

  useEffect(() => {
    const fetchProject = async () => {
      try {
        const response = await projectsAPI.getProject(id);
        setProject(response.data);

        if (response.data?.technologies?.length > 0) {
          const relatedResponse = await projectsAPI.getProjects({
            limit: 5,
            technology: response.data.technologies.join(","),
          });
          const related =
            relatedResponse.data?.filter((p) => p._id !== id) || [];
          setRelatedProjects(related.slice(0, 3));
        } else {
          setRelatedProjects([]);
        }
      } catch (error) {
        console.error(error);
        navigate("/portfolio");
      } finally {
        setLoading(false);
      }
    };
    fetchProject();
  }, [id, navigate]);

  const imageCount = project?.images?.length ?? 0;
  const hasMultipleImages = imageCount > 1;

  const nextImage = useCallback(() => {
    if (!project?.images?.length) return;
    setCurrentImageIndex((prev) =>
      prev === project.images.length - 1 ? 0 : prev + 1
    );
  }, [project]);

  const prevImage = useCallback(() => {
    if (!project?.images?.length) return;
    setCurrentImageIndex((prev) =>
      prev === 0 ? project.images.length - 1 : prev - 1
    );
  }, [project]);

  useEffect(() => {
    if (!hasMultipleImages) return;
    const onKey = (e) => {
      const tag = (e.target && e.target.tagName) || "";
      if (tag === "INPUT" || tag === "TEXTAREA" || e.target?.isContentEditable) return;
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        prevImage();
      }
      if (e.key === "ArrowRight") {
        e.preventDefault();
        nextImage();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [hasMultipleImages, nextImage, prevImage]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <LoadingSpinner size="lg" text="Loading project…" />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-b from-[#fafbfc] to-gray-50 px-4 dark:from-gray-950 dark:to-black">
        <h1 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-gray-50">
          Project not found
        </h1>
        <p className="mt-2 max-w-sm text-center text-sm text-gray-600 dark:text-gray-400">
          This entry may have been removed or the link is out of date.
        </p>
        <Link
          to="/portfolio"
          className="mt-8 inline-flex items-center gap-2 rounded-full border border-gray-900/10 bg-gray-900 px-6 py-3 text-sm font-semibold text-white shadow-md transition hover:bg-gray-800 dark:border-white/10 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100"
        >
          <FiArrowLeft size={18} aria-hidden />
          Back to archive
        </Link>
      </div>
    );
  }

  const formattedDate =
    project.createdAt && !Number.isNaN(Date.parse(project.createdAt))
      ? new Date(project.createdAt).toLocaleDateString(undefined, {
          year: "numeric",
          month: "short",
          day: "numeric",
        })
      : null;

  const currentAlt =
    imageCount > 0
      ? `${project.title} — screenshot ${currentImageIndex + 1} of ${imageCount}`
      : project.title;

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#fafbfc] via-white to-gray-50/90 pb-20 pt-16 text-gray-900 dark:from-gray-950 dark:via-gray-950 dark:to-black dark:text-gray-100">
      <div
        className="pointer-events-none fixed inset-0 opacity-[0.28] dark:opacity-[0.14]"
        aria-hidden="true"
        style={{
          backgroundImage:
            "radial-gradient(circle at 1px 1px, rgb(148 163 184 / 0.2) 1px, transparent 0)",
          backgroundSize: "28px 28px",
        }}
      />

      <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.nav
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="pt-6 pb-8"
          aria-label="Breadcrumb"
        >
          <Link
            to="/portfolio"
            className="inline-flex items-center gap-2 rounded-full border border-gray-200/90 bg-white/90 px-4 py-2 text-sm font-medium text-gray-700 shadow-sm ring-1 ring-black/[0.03] transition hover:border-gray-300 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40 dark:border-gray-800 dark:bg-gray-950/80 dark:text-gray-200 dark:ring-white/[0.04] dark:hover:border-gray-600 dark:hover:bg-gray-900"
          >
            <FiArrowLeft size={16} className="shrink-0 opacity-70" aria-hidden />
            <span className="font-mono text-xs uppercase tracking-[0.12em]">Portfolio</span>
          </Link>
        </motion.nav>

        <article>
          {/* Gallery first — full width hero */}
          <motion.section
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45 }}
            className="mb-10 lg:mb-12 xl:mb-14"
            aria-label="Project gallery"
          >
            <div className="w-full">
              {imageCount > 0 ? (
                <div className="space-y-4">
                  <div
                    className="relative overflow-hidden rounded-2xl border border-black/[0.08] bg-white shadow-[0_20px_50px_-20px_rgba(15,23,42,0.25),0_0_0_1px_rgba(15,23,42,0.04)] ring-1 ring-black/[0.04] dark:border-white/[0.08] dark:bg-[#161b22] dark:shadow-[0_28px_60px_-24px_rgba(0,0,0,0.9),0_0_0_1px_rgba(255,255,255,0.04)] dark:ring-white/[0.06]"
                    role="region"
                    aria-roledescription="carousel"
                    aria-label={`${project.title} image gallery`}
                  >
                    {/* Window chrome — makes the hero read like a deliberate product preview */}
                    <div className="flex h-10 shrink-0 items-center gap-2 border-b border-black/[0.06] bg-[#eceef2] px-3 dark:border-white/[0.08] dark:bg-[#2d333b] sm:h-11 sm:px-3.5">
                      <span className="flex shrink-0 gap-1.5" aria-hidden="true">
                        <span className="h-2.5 w-2.5 rounded-full bg-[#ff5f57] shadow-[inset_0_-1px_0_rgba(0,0,0,0.2)]" />
                        <span className="h-2.5 w-2.5 rounded-full bg-[#febc2e] shadow-[inset_0_-1px_0_rgba(0,0,0,0.15)]" />
                        <span className="h-2.5 w-2.5 rounded-full bg-[#28c840] shadow-[inset_0_-1px_0_rgba(0,0,0,0.15)]" />
                      </span>
                      <div className="flex min-w-0 flex-1 items-center justify-center sm:justify-end">
                        <div className="flex max-w-full items-center gap-2 truncate rounded-md border border-black/[0.08] bg-white px-2.5 py-1 font-mono text-[10px] text-gray-600 shadow-sm dark:border-white/[0.1] dark:bg-[#0d1117] dark:text-gray-400 sm:text-[11px]">
                          <span className="shrink-0 text-gray-400 dark:text-gray-500">~/gallery/</span>
                          <span className="truncate text-gray-800 dark:text-gray-200">
                            {currentImageIndex === 0 ? (
                              <>
                                hero<span className="text-primary-600 dark:text-primary-400">.png</span>
                              </>
                            ) : (
                              <>
                                screen-{String(currentImageIndex + 1).padStart(2, "0")}
                                <span className="text-primary-600 dark:text-primary-400">.png</span>
                              </>
                            )}
                          </span>
                        </div>
                      </div>
                      {hasMultipleImages ? (
                        <span className="inline-flex shrink-0 items-center rounded-md border border-black/[0.06] bg-white/90 px-2 py-0.5 font-mono text-[10px] tabular-nums text-gray-600 shadow-sm dark:border-white/[0.1] dark:bg-[#0d1117]/90 dark:text-gray-400 sm:text-[11px]">
                          {currentImageIndex + 1}/{imageCount}
                        </span>
                      ) : (
                        <span className="inline-flex shrink-0 font-mono text-[10px] uppercase tracking-[0.12em] text-gray-500 dark:text-gray-500">
                          Preview
                        </span>
                      )}
                    </div>

                    <div className="bg-[#f6f8fa] p-1.5 sm:p-2 dark:bg-[#0d1117]">
                      <div className="relative aspect-[16/10] max-h-[min(72vh,50rem)] w-full min-h-[220px] overflow-hidden rounded-xl border border-black/[0.06] bg-[#dfe3e8] shadow-[inset_0_1px_2px_rgba(0,0,0,0.06)] dark:border-white/[0.08] dark:bg-[#010409] dark:shadow-[inset_0_2px_6px_rgba(0,0,0,0.5)]">
                        <motion.img
                          key={currentImageIndex}
                          src={getImageUrl(project.images[currentImageIndex].url)}
                          alt={currentAlt}
                          className="h-full w-full object-cover object-center"
                          initial={{ opacity: 0, scale: 1.03 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                        />
                        <div
                          className="pointer-events-none absolute inset-x-0 top-0 h-14 bg-gradient-to-b from-black/[0.08] to-transparent dark:from-white/[0.07]"
                          aria-hidden
                        />
                        <div
                          className="pointer-events-none absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-black/55 via-black/18 to-transparent dark:from-black/65"
                          aria-hidden
                        />
                        {currentImageIndex === 0 ? (
                          <div className="pointer-events-none absolute left-3 top-3 sm:left-4 sm:top-4">
                            <span className="inline-flex items-center rounded-md border border-white/25 bg-black/45 px-2.5 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-white shadow-md backdrop-blur-md sm:text-[11px]">
                              Cover shot
                            </span>
                          </div>
                        ) : null}

                        {hasMultipleImages ? (
                          <>
                            <button
                              type="button"
                              onClick={prevImage}
                              className="absolute left-2.5 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-white/25 bg-black/50 text-white shadow-lg backdrop-blur-md transition hover:bg-black/70 focus:outline-none focus-visible:ring-2 focus-visible:ring-white sm:left-4 sm:h-11 sm:w-11"
                              aria-label="Previous image"
                            >
                              <FiChevronLeft size={23} strokeWidth={2} />
                            </button>
                            <button
                              type="button"
                              onClick={nextImage}
                              className="absolute right-2.5 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-white/25 bg-black/50 text-white shadow-lg backdrop-blur-md transition hover:bg-black/70 focus:outline-none focus-visible:ring-2 focus-visible:ring-white sm:right-4 sm:h-11 sm:w-11"
                              aria-label="Next image"
                            >
                              <FiChevronRight size={23} strokeWidth={2} />
                            </button>
                            <p className="sr-only" aria-live="polite">
                              Image {currentImageIndex + 1} of {imageCount}
                            </p>
                          </>
                        ) : null}
                      </div>
                    </div>
                  </div>

                  {hasMultipleImages ? (
                    <div className="flex gap-2 overflow-x-auto pb-1 pt-1 [scrollbar-width:thin]">
                      {project.images.map((img, idx) => {
                        const selected = idx === currentImageIndex;
                        return (
                          <button
                            key={idx}
                            type="button"
                            onClick={() => setCurrentImageIndex(idx)}
                            className={`relative shrink-0 overflow-hidden rounded-lg border-2 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/50 ${
                              selected
                                ? "border-primary-600 ring-2 ring-primary-500/25 dark:border-primary-400"
                                : idx === 0
                                  ? "border-amber-400/35 opacity-90 ring-1 ring-amber-500/20 hover:opacity-100 dark:border-amber-500/25"
                                  : "border-transparent opacity-80 hover:opacity-100"
                            }`}
                            aria-label={`Show image ${idx + 1}`}
                            aria-current={selected ? "true" : undefined}
                          >
                            <img
                              src={getImageUrl(img.url)}
                              alt=""
                              className="h-16 w-24 object-cover sm:h-[4.5rem] sm:w-28"
                            />
                          </button>
                        );
                      })}
                    </div>
                  ) : null}

                  {hasMultipleImages ? (
                    <p className="font-mono text-[11px] text-gray-500 dark:text-gray-500">
                      Tip: use arrow keys ← → to move between shots.
                    </p>
                  ) : null}
                </div>
              ) : (
                <div className="flex aspect-[16/10] min-h-[220px] flex-col items-center justify-center rounded-2xl border border-dashed border-gray-300/90 bg-white/60 dark:border-gray-700 dark:bg-gray-950/40">
                  <FiCode className="mb-3 text-gray-300 dark:text-gray-600" size={40} strokeWidth={1.15} aria-hidden />
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300">No gallery for this project</p>
                  <p className="mt-1 max-w-xs px-4 text-center text-xs text-gray-500 dark:text-gray-500">
                    Screenshots can be added from the admin panel when you are ready.
                  </p>
                </div>
              )}
            </div>
          </motion.section>

          <div className="grid gap-10 lg:grid-cols-12 lg:gap-12 xl:gap-14">
            <div className="min-w-0 lg:col-span-8">
              <motion.header
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45, delay: 0.06 }}
                className="max-w-3xl"
              >
                <div className="mb-4 flex flex-wrap items-center gap-3">
                  <span className="font-mono text-xs font-medium tabular-nums text-primary-600 dark:text-primary-400">
                    Case study
                  </span>
                  <span className="h-px w-8 bg-gray-300 dark:bg-gray-600" aria-hidden />
                  {project.featured ? (
                    <span className="rounded-full border border-amber-200/80 bg-amber-50 px-2.5 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-amber-900 dark:border-amber-500/30 dark:bg-amber-950/50 dark:text-amber-200">
                      Featured
                    </span>
                  ) : null}
                </div>

                <h1
                  id="project-detail-title"
                  className="text-3xl font-bold tracking-tight sm:text-4xl md:text-[2.75rem] md:leading-[1.12]"
                >
                  {project.title}
                </h1>

                {project.description ? (
                  <p className="mt-4 text-base leading-relaxed text-gray-600 dark:text-gray-400 md:text-lg">
                    {project.description}
                  </p>
                ) : null}

                <dl className="mt-6 flex flex-wrap gap-x-8 gap-y-3 border-t border-gray-200/90 pt-6 dark:border-gray-800">
                  {formattedDate ? (
                    <div className="flex items-center gap-2">
                      <dt className="sr-only">Published</dt>
                      <FiCalendar className="text-gray-400 dark:text-gray-500" size={16} aria-hidden />
                      <dd className="font-mono text-sm text-gray-600 dark:text-gray-400">{formattedDate}</dd>
                    </div>
                  ) : null}
                  {project.views > 0 ? (
                    <div className="flex items-center gap-2">
                      <dt className="sr-only">Views</dt>
                      <FiEye className="text-gray-400 dark:text-gray-500" size={16} aria-hidden />
                      <dd className="font-mono text-sm text-gray-600 dark:text-gray-400">
                        <span className="tabular-nums">{project.views}</span> views
                      </dd>
                    </div>
                  ) : null}
                </dl>
              </motion.header>
            </div>

            <aside className="lg:col-span-4">
              <div className="space-y-6 lg:sticky lg:top-24">
                <motion.section
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.05 }}
                  className="rounded-2xl border border-gray-200/90 bg-white/90 p-5 shadow-sm ring-1 ring-black/[0.03] dark:border-gray-800 dark:bg-gray-950/70 dark:ring-white/[0.04] md:p-6"
                  aria-labelledby="project-tech-heading"
                >
                  <h2
                    id="project-tech-heading"
                    className="border-b border-gray-100 pb-3 font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-gray-500 dark:border-gray-800 dark:text-gray-500"
                  >
                    Stack & tools
                  </h2>
                  {project.technologies?.length ? (
                    <ul className="mt-4 flex flex-wrap gap-2" aria-label="Technologies used">
                      {project.technologies.map((tech, idx) => (
                        <li key={idx}>
                          <span className="inline-block rounded-md border border-gray-200/90 bg-gray-50/90 px-2.5 py-1 text-xs font-medium text-gray-700 dark:border-gray-700 dark:bg-gray-900/60 dark:text-gray-300">
                            {tech}
                          </span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="mt-4 text-sm text-gray-500 dark:text-gray-500">No technologies listed.</p>
                  )}
                </motion.section>

                <motion.section
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.1 }}
                  className="rounded-2xl border border-gray-200/90 bg-white/90 p-5 shadow-sm ring-1 ring-black/[0.03] dark:border-gray-800 dark:bg-gray-950/70 dark:ring-white/[0.04] md:p-6"
                  aria-labelledby="project-links-heading"
                >
                  <h2
                    id="project-links-heading"
                    className="border-b border-gray-100 pb-3 font-mono text-[10px] font-semibold uppercase tracking-[0.16em] text-gray-500 dark:border-gray-800 dark:text-gray-500"
                  >
                    Links
                  </h2>
                  <div className="mt-4 flex flex-col gap-3">
                    {project.liveUrl ? (
                      <a
                        href={project.liveUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-gray-900/10 bg-gray-900 px-4 py-3.5 text-sm font-semibold text-white shadow-md transition hover:bg-gray-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 dark:border-white/10 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100 dark:focus-visible:ring-offset-gray-950"
                      >
                        <FiExternalLink size={18} aria-hidden />
                        Open live site
                      </a>
                    ) : null}
                    {project.githubUrl ? (
                      <a
                        href={project.githubUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-3.5 text-sm font-semibold text-gray-900 transition hover:border-gray-300 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40 focus-visible:ring-offset-2 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 dark:hover:border-gray-600 dark:hover:bg-gray-800 dark:focus-visible:ring-offset-gray-950"
                      >
                        <FiGithub size={18} aria-hidden />
                        View on GitHub
                      </a>
                    ) : null}
                    {!project.liveUrl && !project.githubUrl ? (
                      <p className="text-sm text-gray-500 dark:text-gray-500">No external links for this entry.</p>
                    ) : null}
                  </div>
                </motion.section>
              </div>
            </aside>
          </div>
        </article>

        {relatedProjects.length > 0 ? (
          <motion.section
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.45 }}
            className="mt-20 border-t border-gray-200/90 pt-16 dark:border-gray-800"
            aria-labelledby="related-heading"
          >
            <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
              <div>
                <p className="mb-2 font-mono text-xs font-medium uppercase tracking-[0.14em] text-primary-600 dark:text-primary-400">
                  Same stack
                </p>
                <h2 id="related-heading" className="text-2xl font-bold tracking-tight text-gray-900 dark:text-gray-50 md:text-3xl">
                  Related projects
                </h2>
                <p className="mt-2 max-w-xl text-sm text-gray-600 dark:text-gray-400">
                  Other work that overlaps with this project’s technologies.
                </p>
              </div>
              <Link
                to="/portfolio"
                className="inline-flex items-center gap-2 text-sm font-semibold text-primary-600 transition hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
              >
                Full archive
                <FiArrowRight size={16} aria-hidden />
              </Link>
            </div>

            <ul className="m-0 grid list-none grid-cols-1 gap-5 p-0 sm:grid-cols-2 lg:grid-cols-3 lg:gap-6">
              {relatedProjects.map((rel) => (
                <li key={rel._id} className="min-w-0">
                  <Link
                    to={`/project/${rel._id}`}
                    className="group flex h-full flex-col overflow-hidden rounded-2xl border border-gray-200/90 bg-white/90 shadow-sm ring-1 ring-black/[0.03] transition hover:border-gray-300 hover:shadow-md dark:border-gray-800 dark:bg-gray-950/70 dark:ring-white/[0.04] dark:hover:border-gray-600"
                  >
                    {rel.images?.[0] ? (
                      <div className="relative aspect-[16/10] overflow-hidden bg-gray-100 dark:bg-gray-900">
                        <img
                          src={getImageUrl(rel.images[0].url)}
                          alt=""
                          className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.03]"
                        />
                        <div
                          className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/35 to-transparent opacity-0 transition group-hover:opacity-100"
                          aria-hidden
                        />
                      </div>
                    ) : (
                      <div className="flex aspect-[16/10] items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-950">
                        <FiCode className="text-gray-300 dark:text-gray-600" size={28} aria-hidden />
                      </div>
                    )}
                    <div className="flex flex-1 flex-col border-t border-gray-100 p-4 dark:border-gray-800 md:p-5">
                      <h3 className="font-semibold leading-snug text-gray-900 transition group-hover:text-primary-600 dark:text-gray-50 dark:group-hover:text-primary-400">
                        {rel.title}
                      </h3>
                      <p className="mt-2 line-clamp-2 flex-1 text-sm leading-relaxed text-gray-600 dark:text-gray-400">
                        {rel.description}
                      </p>
                      <span className="mt-3 inline-flex items-center gap-1 font-mono text-[11px] font-medium uppercase tracking-[0.1em] text-primary-600 dark:text-primary-400">
                        Open
                        <FiArrowRight size={12} className="transition group-hover:translate-x-0.5" aria-hidden />
                      </span>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          </motion.section>
        ) : null}
      </div>
    </div>
  );
};

export default ProjectDetail;
